import { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Pilcrow } from "lucide-react";

const exec = (command, value = null) => document.execCommand(command, false, value);

const ToolbarButton = ({ icon: Icon, label, onClick, testid }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="grid place-items-center h-8 w-8 rounded-md text-slate-600 hover:bg-brand-50 hover:text-brand transition-colors"
  >
    <Icon className="h-4 w-4" strokeWidth={2} />
  </button>
);

export const RichTextEditor = ({ value, onChange, placeholder = "Write the article…" }) => {
  const ref = useRef(null);

  // Sync external value only when it differs (avoids caret jump on each keystroke)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = useCallback(() => {
    if (ref.current) onChange(ref.current.innerHTML);
  }, [onChange]);

  const run = (cmd, val) => { exec(cmd, val); emit(); ref.current?.focus(); };

  const addLink = () => {
    const url = window.prompt("Enter URL (include https://)");
    if (url) run("createLink", url);
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <ToolbarButton icon={Bold} label="Bold" testid="rte-bold" onClick={() => run("bold")} />
        <ToolbarButton icon={Italic} label="Italic" testid="rte-italic" onClick={() => run("italic")} />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton icon={Heading2} label="Heading 2" testid="rte-h2" onClick={() => run("formatBlock", "H2")} />
        <ToolbarButton icon={Heading3} label="Heading 3" testid="rte-h3" onClick={() => run("formatBlock", "H3")} />
        <ToolbarButton icon={Pilcrow} label="Paragraph" testid="rte-p" onClick={() => run("formatBlock", "P")} />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton icon={List} label="Bullet list" testid="rte-ul" onClick={() => run("insertUnorderedList")} />
        <ToolbarButton icon={ListOrdered} label="Numbered list" testid="rte-ol" onClick={() => run("insertOrderedList")} />
        <ToolbarButton icon={Link2} label="Insert link" testid="rte-link" onClick={addLink} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        data-testid="rte-content"
        className="rte-content min-h-[240px] max-h-[420px] overflow-y-auto px-4 py-3 text-slate-800 leading-relaxed focus:outline-none
          [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
          [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-2
          [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
          [&_a]:text-brand [&_a]:underline [&_strong]:font-bold [&_b]:font-bold [&_em]:italic"
      />
    </div>
  );
};
